import React, { useEffect } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

export function Toast({ toast, onRemove }: ToastProps) {
  useEffect(() => {
    const duration = toast.duration ?? 4000;
    const timer = setTimeout(() => {
      onRemove(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  const getStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-green-500/10 dark:bg-green-500/10 border-green-500/30 dark:border-green-500/30',
          icon: CheckCircle2,
          iconColor: 'text-green-400 dark:text-green-400',
          textColor: 'text-green-300 dark:text-green-300',
        };
      case 'error':
        return {
          bg: 'bg-red-500/10 dark:bg-red-500/10 border-red-500/30 dark:border-red-500/30',
          icon: XCircle,
          iconColor: 'text-red-400 dark:text-red-400',
          textColor: 'text-red-300 dark:text-red-300',
        };
      case 'warning':
        return {
          bg: 'bg-amber-500/10 dark:bg-amber-500/10 border-amber-500/30 dark:border-amber-500/30',
          icon: AlertCircle,
          iconColor: 'text-amber-400 dark:text-amber-400',
          textColor: 'text-amber-300 dark:text-amber-300',
        };
      default:
        return {
          bg: 'bg-blue-500/10 dark:bg-blue-500/10 border-blue-500/30 dark:border-blue-500/30',
          icon: Info,
          iconColor: 'text-blue-400 dark:text-blue-400',
          textColor: 'text-blue-300 dark:text-blue-300',
        };
    }
  };

  const styles = getStyles();
  const Icon = styles.icon;

  return (
    <div
      className={`${styles.bg} border rounded-lg p-4 shadow-lg backdrop-blur-sm 
        animate-in slide-in-from-right-full duration-300 flex items-start gap-3 
        min-w-[300px] max-w-md`}
    >
      <Icon className={`w-5 h-5 ${styles.iconColor} flex-shrink-0 mt-0.5`} />
      <p className={`${styles.textColor} text-sm flex-1 break-words`}>{toast.message}</p>
      <button
        onClick={() => onRemove(toast.id)}
        className={`${styles.iconColor} hover:opacity-70 transition-opacity 
          flex-shrink-0 p-1 rounded focus:outline-none focus:ring-2 
          focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-amber-500`}
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 flex flex-col items-end pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}

// Hook for managing toasts
export function useToast() {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const addToast = (message: string, type: ToastType = 'info', duration?: number) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = { id, message, type, duration };
    setToasts((prev) => [...prev, newToast]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const success = (message: string, duration?: number) => addToast(message, 'success', duration);
  const error = (message: string, duration?: number) => addToast(message, 'error', duration || 6000);
  const warning = (message: string, duration?: number) => addToast(message, 'warning', duration);
  const info = (message: string, duration?: number) => addToast(message, 'info', duration);

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };
}

